
import { useState, useEffect, useRef } from 'react';
import { useDarkMode } from '../../App';

interface StatItem {
  value: string;
  suffix?: string;
  label: string;
  sublabel?: string;
}

const stats: StatItem[] = [
  {
    value: '3,000',
    suffix: '+',
    label: 'Properties Professionally Captured',
    sublabel: 'From executive condos to multi-million dollar custom homes across the GTA.',
  },
  {
    value: '350',
    suffix: '+',
    label: 'Agents, Teams & Developers Served',
    sublabel: 'Boutique brokerages to top-producing real estate teams.',
  },
  {
    value: '$4B',
    suffix: '+',
    label: 'Combined Property Value Marketed',
    sublabel: 'Strategic visual launches across a wide range of price points.',
  },
  {
    value: '90%',
    suffix: '+',
    label: 'Repeat & Referral Client Base',
    sublabel: 'Long-term partnerships built on consistency.',
  },
  {
    value: '48–72',
    suffix: ' Hour',
    label: 'Average Turnaround Time',
  },
  {
    value: '5M',
    suffix: '+',
    label: 'Combined Listing & Social Media Views Generated',
  },
];

function AnimatedNumber({ value, suffix, isVisible }: { value: string; suffix?: string; isVisible: boolean }) {
  const [displayValue, setDisplayValue] = useState(value);
  const [opacity, setOpacity] = useState(0);
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (!isVisible || hasAnimated.current) return;
    hasAnimated.current = true;

    // Extract numeric part for animation
    const numericMatch = value.replace(/[$%,]/g, '').match(/[\d.]+/);
    const prefix = value.match(/^[^0-9]*/)?.[0] || '';
    const hasComma = value.includes(',');

    if (numericMatch) {
      const target = parseFloat(numericMatch[0]);
      const duration = 2000;
      const startTime = performance.now();

      const animate = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        // Ease out cubic
        const eased = 1 - Math.pow(1 - progress, 3);
        const current = Math.floor(target * eased);

        let formatted = current.toString();
        if (hasComma && current >= 1000) {
          formatted = current.toLocaleString();
        }

        // Reconstruct with prefix and percent
        let display = prefix + formatted;
        if (value.includes('%')) display += '%';
        if (value.includes('B')) display = prefix + (current >= target ? target : current) + 'B';
        if (value.includes('M')) display = prefix + (current >= target ? target : current) + 'M';

        setDisplayValue(display);
        setOpacity(Math.min(eased * 2, 1));

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          setDisplayValue(value);
          setOpacity(1);
        }
      };

      requestAnimationFrame(animate);
    } else {
      // Non-numeric values like "48–72"
      setDisplayValue(value);
      setOpacity(1);
    }
  }, [isVisible, value]);

  return (
    <span style={{ opacity, transition: 'opacity 0.3s ease' }}>
      {displayValue}{suffix && <span>{suffix}</span>}
    </span>
  );
}

export default function StatsSection() {
  const { isDarkMode } = useDarkMode();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.15 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={sectionRef} className="max-w-7xl mx-auto">
      {/* Section Title */}
      <div
        className="text-center mb-10 md:mb-16 transition-all duration-1000"
        style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
        }}
      >
        <p
          className={`text-[10px] sm:text-xs tracking-[0.3em] uppercase mb-3 md:mb-4 ${
            isDarkMode ? 'text-white/40' : 'text-[#000000]/40'
          }`}
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          CINÉSTATE™ — By The Numbers
        </p>
        <div className={`w-12 h-px mx-auto ${isDarkMode ? 'bg-white/20' : 'bg-[#000000]/20'}`}></div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 lg:gap-10">
        {stats.map((stat, index) => (
          <div
            key={index}
            className={`group relative p-6 md:p-8 lg:p-10 rounded-lg border transition-all duration-700 ${
              isDarkMode
                ? 'border-white/5 hover:border-white/15 bg-white/[0.02] hover:bg-white/[0.04]'
                : 'border-[#000000]/5 hover:border-[#000000]/15 bg-[#000000]/[0.01] hover:bg-[#000000]/[0.03]'
            }`}
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
              transition: `opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1) ${index * 0.12}s, transform 0.8s cubic-bezier(0.16, 1, 0.3, 1) ${index * 0.12}s, border-color 0.3s, background-color 0.3s`,
            }}
          >
            {/* Animated accent line */}
            <div
              className={`absolute top-0 left-6 md:left-8 lg:left-10 h-px transition-all duration-1000 ${
                isDarkMode ? 'bg-white/30' : 'bg-[#000000]/30'
              }`}
              style={{
                width: isVisible ? '40px' : '0px',
                transitionDelay: `${index * 0.12 + 0.5}s`,
              }}
            ></div>

            <div
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light mb-3 md:mb-4 tracking-tight"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              <AnimatedNumber value={stat.value} suffix={stat.suffix} isVisible={isVisible} />
            </div>

            <h4
              className={`text-xs md:text-sm font-normal tracking-wide uppercase mb-2 ${
                isDarkMode ? 'text-white/90' : 'text-[#000000]/90'
              }`}
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              {stat.label}
            </h4>

            {stat.sublabel && (
              <p
                className={`text-xs md:text-sm font-light leading-relaxed ${
                  isDarkMode ? 'text-white/50' : 'text-[#000000]/50'
                }`}
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                {stat.sublabel}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
